import React, {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { gql, useMutation, useLazyQuery } from "@apollo/client";
import {
  CaregiverRelationship,
  Sex,
  Pronouns,
  AppealType,
} from "../__generated__/graphql";
import { parseDateOnly } from "shared/src/utils/dateUtils";
import { useAppealIdFromSession } from "./AppealIdContext";

const GET_PATIENT_AND_CAREGIVER_DATA = gql(`
  query GetPatientAndCaregiverData($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealType
      patient {
        firstName
        lastName
        dateOfBirth
        sex
        pronouns
        phone
        address {
          addressLine1
          addressLine2
          city
          state
          zip
        }
        hasTransactionalSmsConsent
        hasMarketingSmsConsent
      }
      caregiver {
        firstName
        lastName
        dateOfBirth
        relationship
      }
    }
  }
`);

const SAVE_PATIENT_DATA = gql(`
  mutation SavePatientData($appealId: ID!, $patientData: PatientDataInput!) {
    savePatientData(appealId: $appealId, patientData: $patientData)
  }
`);

const SAVE_CAREGIVER_DATA = gql(`
  mutation SaveCaregiverData($appealId: ID!, $caregiver: CaregiverInput!) {
    saveCaregiverData(appealId: $appealId, caregiver: $caregiver)
  }
`);

const UPDATE_APPEAL_TYPE = gql(`
  mutation UpdateAppealType($appealId: ID!, $appealType: AppealType!) {
    updateAppealType(appealId: $appealId, appealType: $appealType)
  }
`);

export type PatientState = {
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: Date | null;
  sex: Sex | null;
  state: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  pronouns: Pronouns | null;
  phoneNumber: string | null;
  hasConsentedToSMSNotifications: boolean | null;
  hasConsentedToMarketingSMS: boolean | null;
};

export type CaregiverState = {
  firstName: string;
  lastName: string;
  dateOfBirth: Date | null;
  relationshipToPatient: CaregiverRelationship;
};

type PatientContextValue = {
  patient: PatientState;
  savePatient: (patientData: PatientState) => Promise<void>;
  caregiver: CaregiverState | null;
  saveCaregiver: (caregiverData: CaregiverState) => Promise<void>;
  appealType: AppealType | null;
  setAppealType: (appealType: AppealType | null) => void;
  saveAppealType: (appealType: AppealType) => Promise<void>;
  loading: boolean;
  mutationLoading: boolean;
};

type PatientProviderProps = {
  children: ReactNode;
};

const PatientContext = createContext<PatientContextValue | null>(null);

export const PatientProvider = ({ children }: PatientProviderProps) => {
  const [patient, setPatient] = useState<PatientState>({
    firstName: null,
    lastName: null,
    dateOfBirth: null,
    sex: null,
    state: null,
    addressLine1: null,
    addressLine2: null,
    city: null,
    zip: null,
    pronouns: null,
    phoneNumber: null,
    hasConsentedToSMSNotifications: null,
    hasConsentedToMarketingSMS: null,
  });
  const [caregiver, stateSetCaregiver] = useState<CaregiverState | null>(null);
  const [appealType, stateSetAppealType] = useState<AppealType | null>(null);
  const [mutationLoading, setMutationLoading] = useState(false);

  const { appealId } = useAppealIdFromSession();

  const [getPatientData, { data: appealData, loading }] = useLazyQuery(
    GET_PATIENT_AND_CAREGIVER_DATA,
    {
      fetchPolicy: "no-cache",
    },
  );

  // Execute query when appealId becomes available
  useEffect(() => {
    if (appealId) {
      getPatientData({
        variables: { appealId },
      });
    }
  }, [appealId]);

  const [savePatientDataMutation] = useMutation(SAVE_PATIENT_DATA);
  const [saveCaregiverDataMutation] = useMutation(SAVE_CAREGIVER_DATA);
  const [updateAppealTypeMutation] = useMutation(UPDATE_APPEAL_TYPE);

  const finalPatient = useMemo(() => {
    if (appealData?.getAppealById?.patient) {
      const apiPatient = appealData.getAppealById.patient;
      return {
        firstName: patient.firstName || apiPatient.firstName,
        lastName: patient.lastName || apiPatient.lastName,
        dateOfBirth:
          patient.dateOfBirth ||
          (apiPatient.dateOfBirth
            ? parseDateOnly(apiPatient.dateOfBirth)
            : null),
        sex: patient.sex || apiPatient.sex,
        state: patient.state || apiPatient.address?.state,
        addressLine1: patient.addressLine1 || apiPatient.address?.addressLine1,
        addressLine2: patient.addressLine2 || apiPatient.address?.addressLine2,
        city: patient.city || apiPatient.address?.city,
        zip: patient.zip || apiPatient.address?.zip,
        pronouns: patient.pronouns || apiPatient.pronouns,
        phoneNumber: patient.phoneNumber || apiPatient.phone,
        hasConsentedToSMSNotifications:
          patient.hasConsentedToSMSNotifications ??
          apiPatient.hasTransactionalSmsConsent,
        hasConsentedToMarketingSMS:
          patient.hasConsentedToMarketingSMS ??
          apiPatient.hasMarketingSmsConsent,
      };
    }
    return patient;
  }, [appealData, patient]);

  const finalCaregiver = useMemo(() => {
    if (appealData?.getAppealById?.caregiver) {
      const apiCaregiver = appealData.getAppealById.caregiver;
      return {
        firstName: caregiver?.firstName || apiCaregiver.firstName || "",
        lastName: caregiver?.lastName || apiCaregiver.lastName || "",
        dateOfBirth:
          caregiver?.dateOfBirth ||
          (apiCaregiver.dateOfBirth
            ? parseDateOnly(apiCaregiver.dateOfBirth)
            : new Date()),
        relationshipToPatient:
          caregiver?.relationshipToPatient ||
          apiCaregiver.relationship ||
          CaregiverRelationship.Mother,
      };
    }
    return caregiver;
  }, [appealData, caregiver]);

  const finalAppealType = useMemo(() => {
    if (appealData?.getAppealById?.appealType) {
      return appealData.getAppealById.appealType;
    }
    return appealType;
  }, [appealData, appealType]);

  const savePatient = async (patientData: PatientState) => {
    setPatient(patientData);

    if (appealId) {
      setMutationLoading(true);
      try {
        await savePatientDataMutation({
          variables: {
            appealId: appealId,
            patientData: {
              firstName: patientData.firstName || "",
              lastName: patientData.lastName || "",
              address: {
                addressLine1: patientData.addressLine1 || "",
                addressLine2: patientData.addressLine2,
                city: patientData.city || "",
                state: patientData.state || "",
                zip: patientData.zip || "",
              },
              sex: patientData.sex,
              pronouns: patientData.pronouns,
              phone: patientData.phoneNumber || null,
              dateOfBirth: patientData.dateOfBirth,
              consentToSms: patientData.hasConsentedToSMSNotifications ?? false,
              consentToMarketingSms:
                patientData.hasConsentedToMarketingSMS ?? false,
            },
          },
        });
      } finally {
        setMutationLoading(false);
      }
    }
  };

  const saveCaregiver = async (caregiverData: CaregiverState) => {
    stateSetCaregiver(caregiverData);

    if (appealId) {
      setMutationLoading(true);
      try {
        await saveCaregiverDataMutation({
          variables: {
            appealId: appealId,
            caregiver: {
              firstName: caregiverData.firstName,
              lastName: caregiverData.lastName,
              dateOfBirth: caregiverData.dateOfBirth,
              relationship: caregiverData.relationshipToPatient,
            },
          },
        });
      } finally {
        setMutationLoading(false);
      }
    }
  };

  const saveAppealType = async (appealTypeValue: AppealType) => {
    stateSetAppealType(appealTypeValue);

    if (appealId) {
      await updateAppealTypeMutation({
        variables: {
          appealId: appealId,
          appealType: appealTypeValue,
        },
      });
    }
  };

  const setAppealType = (appealTypeValue: AppealType | null) =>
    stateSetAppealType(appealTypeValue);

  const value = useMemo(
    () => ({
      patient: finalPatient,
      savePatient,
      caregiver: finalCaregiver,
      saveCaregiver,
      appealType: finalAppealType,
      setAppealType,
      saveAppealType,
      loading,
      mutationLoading,
    }),
    [
      finalPatient,
      savePatient,
      finalCaregiver,
      saveCaregiver,
      finalAppealType,
      saveAppealType,
      loading,
      mutationLoading,
    ],
  );

  return (
    <PatientContext.Provider value={value}>{children}</PatientContext.Provider>
  );
};

export const usePatient = () => {
  const patientContext = useContext(PatientContext);

  if (patientContext === null) {
    throw new Error("usePatient must be used within an PatientProvider");
  }

  return patientContext;
};

export const usePatientContext = usePatient;
